import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet';
import axios from 'axios';
import { FaDownload, FaMobileAlt, FaApple, FaAndroid, FaChrome, FaSafari, FaCheckCircle, FaArrowDown } from 'react-icons/fa';

const API_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

function DownloadApp() {
  const [deviceType, setDeviceType] = useState('unknown');

  useEffect(() => {
    // Detect device type
    const userAgent = navigator.userAgent || navigator.vendor || window.opera;
    
    if (/iPad|iPhone|iPod/.test(userAgent) && !window.MSStream) {
      setDeviceType('ios');
    } else if (/android/i.test(userAgent)) {
      setDeviceType('android');
    } else {
      setDeviceType('desktop');
    }

    // Track app download page view
    trackDownload();
  }, []);

  const trackDownload = async () => {
    try {
      await axios.post(`${API_URL}/api/stats/app-download`);
    } catch (error) {
      console.error('Error tracking download:', error);
    }
  };

  const androidSteps = [
    {
      icon: <FaChrome className="text-3xl" />,
      title: 'مرحله ۱: باز کردن در Chrome',
      description: 'وب‌سایت noafarinsho.ir را در مرورگر Chrome باز کنید'
    },
    {
      icon: <FaDownload className="text-3xl" />,
      title: 'مرحله ۲: نصب اپلیکیشن',
      description: 'روی گزینه "Add to Home Screen" یا "نصب اپلیکیشن" کلیک کنید'
    },
    {
      icon: <FaCheckCircle className="text-3xl" />,
      title: 'مرحله ۳: تایید نصب',
      description: 'روی دکمه "نصب" یا "Install" کلیک کنید تا اپلیکیشن روی صفحه اصلی شما نصب شود'
    }
  ];

  const iosSteps = [
    {
      icon: <FaSafari className="text-3xl" />,
      title: 'مرحله ۱: باز کردن در Safari',
      description: 'وب‌سایت noafarinsho.ir را در مرورگر Safari باز کنید'
    },
    {
      icon: <FaArrowDown className="text-3xl rotate-180" />,
      title: 'مرحله ۲: منوی Share',
      description: 'روی آیکون Share (مربع با فلش به بالا) در پایین صفحه کلیک کنید'
    },
    {
      icon: <FaCheckCircle className="text-3xl" />,
      title: 'مرحله ۳: Add to Home Screen',
      description: 'از لیست گزینه‌ها، "Add to Home Screen" را انتخاب کنید و روی "Add" کلیک کنید'
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-blue-50 to-purple-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900 transition-colors duration-300 py-20">
      <Helmet>
        <title>دانلود اپلیکیشن | نوآفرین‌شو</title>
        <meta name="description" content="دانلود و نصب اپلیکیشن PWA نوآفرین‌شو برای دسترسی آسان‌تر و سریع‌تر" />
      </Helmet>

      <div className="container mx-auto px-4">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="text-center mb-12 md:mb-16"
        >
          <div className="inline-block mb-6">
            <span className="inline-flex items-center gap-3 bg-gradient-to-r from-primary-500 to-primary-600 text-white px-6 py-3 rounded-2xl font-bold text-lg shadow-xl">
              <FaDownload className="text-2xl" />
              دانلود اپلیکیشن
            </span>
          </div>
          <h1 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold mb-6 text-gradient-blue dark:bg-gradient-to-r dark:from-blue-400 dark:to-cyan-300 dark:bg-clip-text dark:text-transparent">
            نصب اپلیکیشن نوآفرین‌شو
          </h1>
          <p className="text-lg md:text-xl lg:text-2xl text-gray-700 dark:text-gray-300 max-w-3xl mx-auto leading-relaxed">
            برای دسترسی سریع‌تر و راحت‌تر، اپلیکیشن نوآفرین‌شو را روی گوشی خود نصب کنید
          </p>
        </motion.div>

        {/* App Icon Preview */}
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="max-w-md mx-auto mb-12"
        >
          <div className="bg-white dark:bg-gray-800 rounded-3xl p-8 shadow-2xl border-2 border-primary-200 dark:border-primary-700 text-center">
            <div className="w-32 h-32 mx-auto mb-4 rounded-3xl overflow-hidden shadow-xl">
              <img 
                src="https://customer-assets.emergentagent.com/job_logo-update-11/artifacts/aj06zws1_Logo%20NOAFARIN%20%20SHO.png"
                alt="آیکون اپلیکیشن نوآفرین‌شو"
                className="w-full h-full object-cover"
              />
            </div>
            <h3 className="text-2xl font-bold text-gray-800 dark:text-gray-200 mb-2">نوآفرین‌شو</h3>
            <p className="text-gray-600 dark:text-gray-400">سامانه ثبت طرح‌های فناور</p>
          </div>
        </motion.div>

        {/* Direct APK Download Section for Android */}
        {(deviceType === 'android' || deviceType === 'unknown' || deviceType === 'desktop') && (
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.3 }}
            className="max-w-3xl mx-auto mb-12"
          >
            <div className="bg-gradient-to-br from-green-500 to-green-600 dark:from-green-600 dark:to-green-700 rounded-3xl p-6 md:p-8 shadow-2xl">
              <div className="text-center mb-6">
                <div className="inline-flex items-center justify-center w-16 h-16 bg-white/20 backdrop-blur-lg rounded-2xl mb-4">
                  <FaAndroid className="text-4xl text-white" />
                </div>
                <h2 className="text-2xl md:text-3xl font-bold text-white mb-2">
                  دانلود مستقیم APK اندروید
                </h2>
                <p className="text-white/90 text-base md:text-lg">
                  فایل نصبی اپلیکیشن را مستقیماً دانلود و نصب کنید
                </p>
              </div>
              
              <a
                href="https://customer-assets.emergentagent.com/job_react-hooks-fix-1/artifacts/7wpkczzw_%D9%86%D9%88%D8%A2%D9%81%D8%B1%DB%8C%D9%86%20%D8%B4%D9%88.apk"
                download="noafarinsho-latest.apk"
                className="block w-full bg-white hover:bg-gray-100 text-green-600 font-bold text-lg py-4 px-6 rounded-2xl shadow-xl hover:shadow-2xl transition-all duration-300 text-center"
                data-testid="download-apk-btn"
              >
                <div className="flex items-center justify-center gap-3">
                  <FaDownload className="text-2xl" />
                  <span>دانلود فایل APK</span>
                </div>
              </a>
              
              <div className="mt-6 bg-white/10 backdrop-blur-lg rounded-2xl p-4">
                <div className="flex items-start gap-3 text-white/90 text-sm">
                  <FaCheckCircle className="text-xl flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="font-semibold mb-1">توجه:</p>
                    <p>پس از دانلود، اجازه نصب از منابع نامشخص را در تنظیمات گوشی خود فعال کنید.</p>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        )}

        {/* Android Instructions */}
        {(deviceType === 'android' || deviceType === 'unknown' || deviceType === 'desktop') && (
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.4 }}
            className="max-w-5xl mx-auto mb-12"
          >
            <div className="bg-gradient-to-br from-white to-green-50 dark:from-gray-800 dark:to-gray-700 rounded-3xl p-6 md:p-10 shadow-2xl border-2 border-green-200 dark:border-green-700">
              <div className="text-center mb-8">
                <h2 className="text-3xl md:text-4xl font-bold text-green-800 dark:text-green-300 mb-3 flex items-center justify-center gap-3">
                  <FaAndroid className="text-4xl" />
                  نصب به صورت PWA در Android
                </h2>
                <p className="text-gray-700 dark:text-gray-300">راهنمای گام به گام برای نصب از طریق مرورگر</p>
              </div>

              <div className="grid md:grid-cols-3 gap-6">
                {androidSteps.map((step, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, delay: 0.5 + index * 0.1 }}
                    className="bg-white dark:bg-gray-700 rounded-2xl p-6 shadow-lg border-2 border-green-200 dark:border-green-600"
                  >
                    <div className="w-16 h-16 bg-gradient-to-br from-green-500 to-green-600 rounded-2xl flex items-center justify-center text-white mb-4 shadow-lg">
                      {step.icon}
                    </div>
                    <h3 className="text-xl font-bold text-gray-800 dark:text-gray-200 mb-3">{step.title}</h3>
                    <p className="text-gray-600 dark:text-gray-400 leading-relaxed">{step.description}</p>
                  </motion.div>
                ))}
              </div>

              {/* Visual Guide for Android */}
              <div className="mt-8 bg-green-100 dark:bg-green-900/30 rounded-2xl p-6 border-2 border-green-300 dark:border-green-600">
                <div className="flex items-start gap-4">
                  <FaCheckCircle className="text-3xl text-green-600 dark:text-green-400 flex-shrink-0" />
                  <div>
                    <h4 className="text-lg font-bold text-gray-800 dark:text-gray-200 mb-2">نکات مهم:</h4>
                    <ul className="space-y-2 text-gray-700 dark:text-gray-300">
                      <li>• حتماً از مرورگر Chrome استفاده کنید</li>
                      <li>• پس از نصب، آیکون اپلیکیشن روی صفحه اصلی گوشی شما ظاهر می‌شود</li>
                      <li>• می‌توانید مانند سایر اپلیکیشن‌ها از آن استفاده کنید</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        )}

        {/* iOS Instructions */}
        {(deviceType === 'ios' || deviceType === 'unknown' || deviceType === 'desktop') && (
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.6 }}
            className="max-w-5xl mx-auto"
          >
            <div className="bg-gradient-to-br from-white to-blue-50 dark:from-gray-800 dark:to-gray-700 rounded-3xl p-6 md:p-10 shadow-2xl border-2 border-blue-200 dark:border-blue-700">
              <div className="text-center mb-8">
                <h2 className="text-3xl md:text-4xl font-bold text-blue-800 dark:text-blue-300 mb-3 flex items-center justify-center gap-3">
                  <FaApple className="text-4xl" />
                  نصب در iPhone/iPad
                </h2>
                <p className="text-gray-700 dark:text-gray-300">راهنمای گام به گام برای دستگاه‌های iOS</p>
              </div>

              <div className="grid md:grid-cols-3 gap-6">
                {iosSteps.map((step, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, delay: 0.7 + index * 0.1 }}
                    className="bg-white dark:bg-gray-700 rounded-2xl p-6 shadow-lg border-2 border-blue-200 dark:border-blue-600"
                  >
                    <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-blue-600 rounded-2xl flex items-center justify-center text-white mb-4 shadow-lg">
                      {step.icon}
                    </div>
                    <h3 className="text-xl font-bold text-gray-800 dark:text-gray-200 mb-3">{step.title}</h3>
                    <p className="text-gray-600 dark:text-gray-400 leading-relaxed">{step.description}</p>
                  </motion.div>
                ))}
              </div>

              {/* Visual Guide for iOS */}
              <div className="mt-8 bg-blue-100 dark:bg-blue-900/30 rounded-2xl p-6 border-2 border-blue-300 dark:border-blue-600">
                <div className="flex items-start gap-4">
                  <FaCheckCircle className="text-3xl text-blue-600 dark:text-blue-400 flex-shrink-0" />
                  <div>
                    <h4 className="text-lg font-bold text-gray-800 dark:text-gray-200 mb-2">نکات مهم:</h4>
                    <ul className="space-y-2 text-gray-700 dark:text-gray-300">
                      <li>• حتماً از مرورگر Safari استفاده کنید</li>
                      <li>• آیکون Share معمولاً در پایین مرورگر قرار دارد</li>
                      <li>• پس از اضافه کردن، آیکون روی Home Screen شما ظاهر می‌شود</li>
                      <li>• می‌توانید نام اپلیکیشن را قبل از اضافه کردن تغییر دهید</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        )}

        {/* Benefits Section */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
          className="max-w-5xl mx-auto mt-12"
        >
          <div className="bg-gradient-to-br from-white to-purple-50 dark:from-gray-800 dark:to-gray-700 rounded-3xl p-6 md:p-10 shadow-2xl border-2 border-purple-200 dark:border-purple-700">
            <h2 className="text-3xl md:text-4xl font-bold text-purple-800 dark:text-purple-300 mb-8 text-center">
              مزایای استفاده از اپلیکیشن
            </h2>
            <div className="grid md:grid-cols-2 gap-6">
              <div className="flex items-start gap-4 bg-white dark:bg-gray-700 rounded-2xl p-5 shadow-lg">
                <div className="w-12 h-12 bg-gradient-to-br from-purple-500 to-purple-600 rounded-xl flex items-center justify-center text-white flex-shrink-0">
                  <FaMobileAlt className="text-2xl" />
                </div>
                <div>
                  <h3 className="text-lg font-bold text-gray-800 dark:text-gray-200 mb-2">دسترسی سریع</h3>
                  <p className="text-gray-600 dark:text-gray-400">با یک کلیک از صفحه اصلی گوشی خود به سامانه دسترسی داشته باشید</p>
                </div>
              </div>

              <div className="flex items-start gap-4 bg-white dark:bg-gray-700 rounded-2xl p-5 shadow-lg">
                <div className="w-12 h-12 bg-gradient-to-br from-purple-500 to-purple-600 rounded-xl flex items-center justify-center text-white flex-shrink-0">
                  <FaCheckCircle className="text-2xl" />
                </div>
                <div>
                  <h3 className="text-lg font-bold text-gray-800 dark:text-gray-200 mb-2">تجربه بهتر</h3>
                  <p className="text-gray-600 dark:text-gray-400">رابط کاربری بهینه‌شده مخصوص موبایل برای استفاده راحت‌تر</p>
                </div>
              </div>

              <div className="flex items-start gap-4 bg-white dark:bg-gray-700 rounded-2xl p-5 shadow-lg">
                <div className="w-12 h-12 bg-gradient-to-br from-purple-500 to-purple-600 rounded-xl flex items-center justify-center text-white flex-shrink-0">
                  <FaDownload className="text-2xl" />
                </div>
                <div>
                  <h3 className="text-lg font-bold text-gray-800 dark:text-gray-200 mb-2">بدون نیاز به استور</h3>
                  <p className="text-gray-600 dark:text-gray-400">نصب مستقیم بدون نیاز به دانلود از App Store یا Play Store</p>
                </div>
              </div>

              <div className="flex items-start gap-4 bg-white dark:bg-gray-700 rounded-2xl p-5 shadow-lg">
                <div className="w-12 h-12 bg-gradient-to-br from-purple-500 to-purple-600 rounded-xl flex items-center justify-center text-white flex-shrink-0">
                  <FaMobileAlt className="text-2xl" />
                </div>
                <div>
                  <h3 className="text-lg font-bold text-gray-800 dark:text-gray-200 mb-2">حجم کم</h3>
                  <p className="text-gray-600 dark:text-gray-400">فضای بسیار کمی از گوشی شما اشغال می‌کند</p>
                </div>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
}

export default DownloadApp;